import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
  AppBar,
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/DashboardOutlined";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonthOutlined";
import EventNoteIcon from "@mui/icons-material/EventNoteOutlined";
import ContactPhoneIcon from "@mui/icons-material/ContactPhoneOutlined";
import DescriptionIcon from "@mui/icons-material/DescriptionOutlined";
import AccountBalanceIcon from "@mui/icons-material/AccountBalanceOutlined";
import PaymentsIcon from "@mui/icons-material/PaymentsOutlined";
import GroupsIcon from "@mui/icons-material/GroupsOutlined";
import StorefrontIcon from "@mui/icons-material/StorefrontOutlined";
import StarIcon from "@mui/icons-material/StarOutlineOutlined";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import LockResetIcon from "@mui/icons-material/LockResetOutlined";
import LogoutIcon from "@mui/icons-material/LogoutOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import { doc, onSnapshot } from "firebase/firestore";
import { signOut } from "firebase/auth";
import { auth, db } from "../lib/firebase";
import { useSession } from "../auth/AuthProvider";
import { formatJalali, kabulToday } from "../lib/format";
import { ChangePasswordDialog } from "../components/ChangePasswordDialog";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { useT } from "../i18n";
import type { StringKey } from "../i18n/fa";

const DRAWER_WIDTH = 252;

// Labels/captions are string keys resolved per-locale at render time.
const NAV_GROUPS: {
  captionKey: StringKey;
  items: { to: string; labelKey: StringKey; icon: JSX.Element }[];
}[] = [
  {
    captionKey: "nav_group_ops",
    items: [
      { to: "/", labelKey: "nav_dashboard", icon: <DashboardIcon /> },
      { to: "/calendar", labelKey: "nav_calendar", icon: <CalendarMonthIcon /> },
      { to: "/bookings", labelKey: "nav_bookings", icon: <EventNoteIcon /> },
      { to: "/contracts", labelKey: "nav_contracts", icon: <DescriptionIcon /> },
      { to: "/crm", labelKey: "nav_crm", icon: <ContactPhoneIcon /> },
    ],
  },
  {
    captionKey: "nav_group_finance",
    items: [
      { to: "/finance", labelKey: "nav_finance", icon: <AccountBalanceIcon /> },
      { to: "/payouts", labelKey: "nav_payouts", icon: <PaymentsIcon /> },
      { to: "/hr", labelKey: "nav_hr", icon: <GroupsIcon /> },
    ],
  },
  {
    captionKey: "nav_group_market",
    items: [
      { to: "/halls", labelKey: "nav_halls", icon: <StorefrontIcon /> },
      { to: "/reviews", labelKey: "nav_reviews", icon: <StarIcon /> },
    ],
  },
];

export function DashboardLayout() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  // Desktop sidebar collapse state, remembered across sessions.
  const [desktopOpen, setDesktopOpen] = useState(
    () => localStorage.getItem("talar.sidebar") !== "collapsed",
  );
  const [passwordOpen, setPasswordOpen] = useState(false);
  const { role, orgId, user } = useSession();
  const [orgName, setOrgName] = useState<string>("");
  const navigate = useNavigate();
  const t = useT();
  const today = kabulToday();

  function toggleSidebar() {
    if (isDesktop) {
      setDesktopOpen((v) => {
        localStorage.setItem("talar.sidebar", v ? "collapsed" : "open");
        return !v;
      });
    } else {
      setMobileOpen((v) => !v);
    }
  }

  useEffect(() => {
    if (!orgId) return;
    return onSnapshot(doc(db, "organizations", orgId), (snap) => {
      setOrgName((snap.data()?.name as string) ?? "");
    });
  }, [orgId]);

  const drawer = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ px: 3, pt: 3, pb: 2 }}>
        <Typography variant="h5" color="primary">
          {t("app_name")}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {orgName || t("layout_panel_fallback")}
        </Typography>
      </Box>
      <Divider />
      <Box sx={{ flex: 1, overflowY: "auto", py: 1 }}>
        {NAV_GROUPS.map((group) => (
          <Box key={group.captionKey} mb={0.5}>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ px: 3, fontSize: 12 }}
            >
              {t(group.captionKey)}
            </Typography>
            <List dense disablePadding>
              {group.items.map((item) => (
                <ListItemButton
                  key={item.to}
                  component={NavLink}
                  to={item.to}
                  end={item.to === "/"}
                  onClick={() => setMobileOpen(false)}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={t(item.labelKey)} />
                </ListItemButton>
              ))}
            </List>
          </Box>
        ))}
        {role === "admin" && (
          <>
            <Typography variant="overline" color="text.secondary" sx={{ px: 3, fontSize: 12 }}>
              {t("nav_group_platform")}
            </Typography>
            <List dense disablePadding>
              <ListItemButton component={NavLink} to="/admin">
                <ListItemIcon>
                  <AdminPanelSettingsIcon />
                </ListItemIcon>
                <ListItemText primary={t("nav_admin")} />
              </ListItemButton>
            </List>
          </>
        )}
      </Box>
      <Divider />
      <Box sx={{ p: 1.5 }}>
        <ListItemButton
          onClick={() => {
            setMobileOpen(false);
            setPasswordOpen(true);
          }}
        >
          <ListItemIcon>
            <LockResetIcon />
          </ListItemIcon>
          <ListItemText primary={t("nav_change_password")} />
        </ListItemButton>
        <ListItemButton
          onClick={async () => {
            await signOut(auth);
            navigate("/login");
          }}
        >
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText
            primary={t("nav_logout")}
            secondary={user?.email ?? ""}
            secondaryTypographyProps={{ fontSize: 11, dir: "ltr", textAlign: "left" }}
          />
        </ListItemButton>
      </Box>
    </Box>
  );

  // How much room the permanent sidebar occupies right now (0 when collapsed).
  const sidebarSpace = isDesktop && desktopOpen ? DRAWER_WIDTH : 0;

  return (
    <Box sx={{ display: "flex" }}>
      <AppBar
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          width: `calc(100% - ${sidebarSpace}px)`,
          ml: `${sidebarSpace}px`,
          transition: theme.transitions.create(["width", "margin"], {
            duration: theme.transitions.duration.shorter,
          }),
        }}
      >
        <Toolbar variant="dense" sx={{ minHeight: 52 }}>
          <IconButton edge="start" onClick={toggleSidebar} sx={{ mr: 1 }} aria-label={t("common_menu")}>
            <MenuIcon />
          </IconButton>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {orgName}
            </Typography>
          </Stack>
          <LanguageSwitcher edge="end" />
          <Chip
            size="small"
            variant="outlined"
            label={t("layout_today", { date: formatJalali(today) })}
            sx={{ ml: 1 }}
          />
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{ display: { md: "none" } }}
        PaperProps={{ sx: { width: DRAWER_WIDTH } }}
      >
        {drawer}
      </Drawer>
      <Drawer
        variant="persistent"
        anchor="left"
        open={isDesktop && desktopOpen}
        sx={{
          display: { xs: "none", md: "block" },
          "& .MuiDrawer-paper": { width: DRAWER_WIDTH, borderRight: 1, borderColor: "divider" },
        }}
      >
        {drawer}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          // Without this, a wide child (a table) refuses to shrink and
          // overflows the flex row — in RTL that clips the start of headers.
          minWidth: 0,
          overflowX: "hidden",
          px: { xs: 2, md: 4 },
          py: 3,
          mt: 6.5,
          minHeight: "100vh",
          bgcolor: "background.default",
          ml: `${sidebarSpace}px`,
          transition: theme.transitions.create("margin", {
            duration: theme.transitions.duration.shorter,
          }),
        }}
      >
        <Box sx={{ maxWidth: 1240, mx: "auto" }}>
          <Outlet />
        </Box>
      </Box>
      <ChangePasswordDialog open={passwordOpen} onClose={() => setPasswordOpen(false)} />
    </Box>
  );
}
